import React, { useEffect, useRef, useState } from 'react';
import throttle from 'lodash/throttle';
import { browserSupports } from 'src/components/base';

const ViewportTrackerContext = React.createContext({});
ViewportTrackerContext.displayName = 'ViewportTrackerContext';

interface ViewportTrackerProps {
  children: React.ReactNode;
}

export const ViewportTracker: React.FC<ViewportTrackerProps> = ({
  children,
}) => {
  const state = useRef<{
    width: number;
    height: number;
    left: number;
    top: number;
    scroll: {
      xDirection: number | undefined;
      yDirection: number | undefined;
      x: number;
      y: number;
    };
  }>({
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    scroll: {
      xDirection: undefined,
      yDirection: undefined,
      x: -0,
      y: -0,
    },
  });

  const [viewport, setViewport] = useState(state.current);

  useEffect(() => {
    function onUpdateViewport() {
      const html = document.documentElement;
      const x = window.scrollX;
      const y = window.scrollY;
      const scrollChanged =
        x !== state.current.scroll.x || y !== state.current.scroll.y;
      const scroll = scrollChanged
        ? {
            xDirection: Math.sign(state.current.scroll.x - x),
            yDirection: Math.sign(state.current.scroll.y - y),
            x,
            y,
          }
        : state.current.scroll;
      state.current = {
        width: window.innerWidth || html.clientWidth,
        height: window.innerHeight || html.clientHeight,
        left: window.pageXOffset || html.scrollLeft,
        top: window.pageYOffset || html.scrollTop,
        scroll,
      };

      setViewport(state.current);
    }

    const throttledOnUpdateViewport = throttle(onUpdateViewport, 200);

    window.addEventListener(
      'scroll',
      throttledOnUpdateViewport,
      browserSupports.passiveEventListener() ? { passive: true } : false
    );

    onUpdateViewport();

    return () => {
      window.removeEventListener('scroll', throttledOnUpdateViewport);
    };
  }, []);

  return (
    <ViewportTrackerContext.Provider value={viewport}>
      {children}
    </ViewportTrackerContext.Provider>
  );
};

export const withViewport = <
  P extends ViewportTrackerProps = ViewportTrackerProps
>(
  Component: React.ComponentType<P>
): React.ComponentType<P> => (props: P) => (
  <ViewportTrackerContext.Consumer>
    {viewport => <Component {...props} viewport={viewport} />}
  </ViewportTrackerContext.Consumer>
);
